/**
 * THE ELEMENT THAT ACTUALLY SCROLLS, found by what it does, not by a named
 * area. At a short or zoomed height (30em) and with large text the step's own
 * area stops scrolling and the page does (v3.css), so a class name would be
 * wrong exactly where it matters (review of ea16b8fc, P2-1).
 */
export function scrollerOf(element: HTMLElement): HTMLElement | null {
  for (let node = element.parentElement; node && node !== document.body; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if ((overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight) return node;
  }
  return null;
}

/** Instant when the reader asked for reduced motion. */
export function scrollBehavior(): ScrollBehavior {
  const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  return reduce ? "auto" : "smooth";
}

const MARGIN = 16;

/**
 * BRING A NOTICE INTO VIEW INSIDE ITS OWN SCROLLER, moving nothing else (rc.10
 * M1). `scrollIntoView` also moved an ancestor in Chromium (screenshot review,
 * rc.9), so the scroller is scrolled by exactly the distance that shows the
 * notice: its bottom when it sits below, its top when it sits above or is
 * taller than the scroller. Never focus: a notice is an alert, announced as it
 * appears. When the document itself scrolls, `scrollIntoView` is the right
 * tool and nothing else can move.
 */
export function revealInScroller(element: HTMLElement, align: "nearest" | "start" = "nearest"): void {
  const behavior = scrollBehavior();
  const scroller = scrollerOf(element);
  const box = element.getBoundingClientRect();
  if (!scroller) {
    // Already whole on screen: nothing moves, whichever the alignment.
    if (align === "start" && box.top >= 0 && box.bottom <= window.innerHeight) return;
    element.scrollIntoView?.({ block: align, behavior });
    return;
  }
  const view = scroller.getBoundingClientRect();
  let delta = 0;
  // "start" (rc.11 F2, the Web Director): a card the person must read has its
  // top at the scroller's top, so the most of it shows and whatever follows
  // sits as close below as the screen allows.
  if (align === "start" && (box.bottom > view.bottom || box.top < view.top)) delta = box.top - view.top - MARGIN;
  else if (box.bottom > view.bottom) delta = Math.min(box.bottom - view.bottom + MARGIN, box.top - view.top - MARGIN);
  else if (box.top < view.top) delta = box.top - view.top - MARGIN;
  if (delta !== 0) scroller.scrollBy?.({ top: delta, behavior });
}
